// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import DefaultCard from "src/components/shared/layouts/DefaultCard/DefaultCard";

// Styles
import { getFeatureRowLabelColor } from "src/styles/mui/muiStylesHelper";

const ColorVision = () => {
  const { t } = useTranslation("operating-manual");
  const theme = useTheme();
  const rowLabelColor = getFeatureRowLabelColor(theme);

  const tableContent = [
    {
      title: t("Ishihara 12-Plate"),
      content: t(
        "A shortened screening version of the Ishihara test that contains 12 plates. It is designed for quick detection of red-green color vision deficiencies"
      ),
    },
    {
      title: t("Ishihara 24-Plate"),
      content: t(
        "A more comprehensive version with 24 plates. Compared to the 12-plate edition, it allows for better differentiation between normal vision and various types of color vision deficiencies, though it is still primarily a screening tool rather than a full diagnostic test"
      ),
    },
    {
      title: t("Saturated D-15"),
      content: t(
        "15 colored discs arranged in order to create a smooth color sequence. The pattern of errors helps identify the type and severity of color vision deficiency"
      ),
    },
    {
      title: t("Desaturated D-15"),
      content: t(
        "15 colored discs with muted, less intense colors, arranged in order to create a smooth color sequence. The pattern of errors helps identify the type and severity of color vision deficiency"
      ),
    },
  ];

  const getTableRow = useCallback(
    (title: string, content: string) => (
      <Grid container key={title}>
        <Grid
          item
          xs={6}
          sm={3}
          sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
        >
          <Typography color={rowLabelColor} fontSize="14px" fontWeight={600}>
            {t(title)}
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          sm={9}
          sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
        >
          <Typography fontSize="14px">{t(content)}</Typography>
        </Grid>
      </Grid>
    ),
    [t, rowLabelColor]
  );

  return (
    <DefaultCard>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Grid container>
          <Grid
            item
            xs={6}
            sm={3}
            sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <Typography fontWeight={600}>{t("Features")}</Typography>
          </Grid>
          <Grid
            item
            xs={6}
            sm={9}
            sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <Typography fontWeight={600}>{t("Description")}</Typography>
          </Grid>
        </Grid>

        {tableContent.map((tableItem) =>
          getTableRow(tableItem.title, tableItem.content)
        )}
      </Box>
    </DefaultCard>
  );
};

export default ColorVision;
